import React from "react"
import styled, { css, keyframes } from "styled-components"
import PropTypes from "prop-types"
import { transparentize } from "polished"
import { HeadingThree, Paragraph } from "buoy-text"
import Button from "buoy-button"
import {
  fonts,
  fontFamily,
  fontSize,
  fontWeight,
  colors,
  responsive,
  constants,
} from "buoy-theme"
import CloseIcon from "buoy-close-icon"

const DialogBox = styled.div`
  position: relative;
  background-color: ${colors.white};
  border-radius: ${constants.borderRadius};
  padding: 1.5rem;
  width: 90vw;
  @media (max-width: 500px) {
    width: 98vw;
  }
  max-width: ${props => (!props.covidModal ? "24.889rem" : "1200px")};
  ${props => props.covidModal && css`
    height: 55vh;
  `};
  ${props =>
    props.covidModal &&
    css`
      // general device media sizes
      @media (min-width: 600px) and (max-width: 1100px) {
        height: 55vh;
      }
      @media (min-width: 350px) and (max-width: 600px) {
        height: auto;
      }
      @media (max-height: 650px) {
          height: 80vh;
      }
      @media (min-height: 1100px) and (min-width: 1000px) {
        height: 60vh;
      }
      @media (min-height: 1200px) and (min-width: 1000px) {
        height: 50vh;
      }
      // common devices specifically
      // iphone8 size
      @media only screen and (device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) {
        height: 87.5vh;
        width: 95vw;
      }
      // iphone 6/7/8 plus
      @media only screen and (device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) {
        height: 85vh;
      }
      // iphone X/11
      @media only screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) {
        height: 75vh;
      }
      // iphone 11 pro/X/Xs
      @media only screen and (device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) {
        height: 80vh;
        width: 94vw;
      }
      //   max size phones
      @media only screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) {
        height: 75vh;
      }

    `};
  box-shadow: ${constants.boxShadow};
  ${props =>
    props.covidModal
      ? "padding: 0"
      : responsive(
          "padding",
          "48px 40px 40px",
          "48px 40px 40px",
          "48px 40px 40px",
          "48px 40px 40px",
          "48px 24px 24px"
        )};
  z-index: 9999;
  transition: transform 0.35s ease-out, opacity 0.15s ease-in;
  transform: ${props => (props.show ? "scale(1.0)" : "scale(0.9)")};
  opacity: ${props => (props.show ? "1" : "0")};
`

const Buttons = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

const CancelButton = styled.a`
  ${fontSize("bodyMain")};
  font-family: ${fontFamily};
  display: inline-block;
  color: #646464;

  &:hover {
    cursor: pointer;
  }
`
const ModalParagraph = styled(Paragraph)`
  line-height: 1.25;
`

const ModalBackground = styled.div`
  position: fixed;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props =>
    props.show
      ? transparentize(0.5, colors.neutralDarkest(props))
      : "transparent"};
  transition: ${props =>
    props.show ? "background 0.35s ease-in" : "background 0.25s ease-in"};
  z-index: 9998;
  overflow-y: auto;
  > * {
    margin: auto 16px;
  }
`

const Dialog = ({
  children,
  heading,
  message,
  showSubmit,
  submitEnabled,
  submitText,
  submitClick,
  showCancel,
  cancelText,
  fadeAway,
  show,
  covidModal,
}) => (
  <DialogBox onClick={e => e.stopPropagation()} show={show} covidModal={covidModal}>
    <HeadingThree fontSize="h3" weight="light" alignLeft margin="0">
      {heading}
    </HeadingThree>

    {message && <ModalParagraph margin="12px 0">{message}</ModalParagraph>}

    <div style={{ height: "100%" }}>{children}</div>

    {(showSubmit || showCancel) && (
      <Buttons>
        {showCancel && (
          <CancelButton onClick={fadeAway}>{cancelText}</CancelButton>
        )}

        {showSubmit && (
          <Button
            margin="0 0 0 24px"
            disabled={!submitEnabled}
            display="block"
            onClick={submitClick}
          >
            {submitText}
          </Button>
        )}
      </Buttons>
    )}

    <CloseIcon
      top="14px"
      right="14px"
      onClick={fadeAway}
      color={covidModal ? "#196cff" : "black"}
      iconSize="16px"
    />
  </DialogBox>
)

class Modal extends React.Component {
  constructor() {
    super()
    this.state = {
      showDialog: false,
      showBackground: false,
      modalClosed: false,
    }
  }

  toggleDialog = () => {
    // we want a transition effect where the background blurs in first and then
    // the modal pops in
    this.state.showDialog
      ? this.setState({ showDialog: false })
      : this.setState({ showDialog: true })
  }

  toggleBackground = () => {
    // we want a transition effect where the background blurs in first and then
    // the modal pops in
    this.state.showBackground
      ? this.setState({ showBackground: false })
      : this.setState({ showBackground: true })
  }

  componentDidMount = () => {
    setTimeout(this.toggleBackground, 100)
    setTimeout(this.toggleDialog, 350)
  }

  fadeAway = () => {
    this.setState({ modalClosed: true })
    this.toggleDialog()
    this.toggleBackground()
    this.props.handleModalClose()
    setTimeout(this.props.cancelClick, 350)
  }

  render() {
    return (
      <ModalBackground
        show={this.state.showBackground}
        onClick={
          this.props.closeOnOutsideClick && !this.state.modalClosed
            ? this.fadeAway
            : null
        }
      >
        <Dialog
          fadeAway={this.fadeAway}
          show={this.state.showDialog}
          {...this.props}
        />
      </ModalBackground>
    )
  }
}

Modal.propTypes = {
  heading: PropTypes.string.isRequired,
  message: PropTypes.string,
  showSubmit: PropTypes.bool,
  submitEnabled: PropTypes.bool,
  submitText: PropTypes.string,
  submitClick: PropTypes.func,
  showCancel: PropTypes.bool,
  cancelClick: PropTypes.func.isRequired,
  cancelText: PropTypes.string,
  closeOnOutsideClick: PropTypes.bool,
}

Modal.defaultProps = {
  showSubmit: true,
  submitEnabled: true,
  showCancel: true,
  cancelText: "Cancel",
  closeOnOutsideClick: false,
}

export { Modal as default }



// WEBPACK FOOTER //
// ./src/components/ModalOverlay/Modal.js